import React, { Component, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import RichTextEditor from 'react-rte';
import axios from 'axios';
import ContentLoader from 'react-content-loader';
import Modal from 'react-modal';
import 'url-search-params-polyfill';
import onClickOutside from 'react-onclickoutside';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { withTranslation, I18nextProvider, useTranslation, Trans } from 'react-i18next';

import { v4 as uuidv4 } from 'uuid';
import { Typography, styled } from '@mui/material';
import DOMPurify from 'dompurify'
import { replaceS3Key } from '../../utils/services';
import { WbEventHandlerMixins, validateImageFileExtension } from '../../utils/common';
import i18n from '../../i18n';
import { updateMemberProfileAndCover } from '../../services/fileupload.service';
import { ProfileManagerLens } from './profileManagerLens';
import { AgentActionItems } from './agentActionItems';
import { ErrorBoundary } from 'react-error-boundary';
import { loadFederatedModule } from '../../utils/loadFederatedModule';

const CheckInsCard = React.lazy(async () => loadFederatedModule('SC/CheckInsCard'));

const StyledMemberReportWrapper = styled("div")({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const MyLoader = props => (
  <ContentLoader
    height={600}
    width={1024}
    speed={2}
    primaryColor="#ffffff"
    secondaryColor="#ecebeb"
    {...props}
  >
    <circle cx="94" cy="160" r="48"/>
    <rect x="100.34" y="36.61" rx="0" ry="0" width="0" height="0"/>
    <rect x="17.13" y="14.61" rx="0" ry="0" width="993.65" height="146.64"/>
    <rect x="29.44" y="241.61" rx="0" ry="0" width="323" height="135.85"/>
    <rect x="30.44" y="404.61" rx="0" ry="0" width="323" height="127.49"/>
    <rect x="388.44" y="240.61" rx="0" ry="0" width="323" height="135.85"/>
    <rect x="387.44" y="402.61" rx="0" ry="0" width="323" height="127.49"/>
    <rect x="783.44" y="237.61" rx="0" ry="0" width="188" height="139"/>
    <rect x="784.44" y="391.61" rx="0" ry="0" width="188" height="139"/>
  </ContentLoader>
);

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxHeight: '75%'
  }
};

const toolbarConfig = {
  // Optionally specify the groups to display (displayed in the order listed).
  display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
  INLINE_STYLE_BUTTONS: [
    {label: 'Italic', style: 'ITALIC'},
    {label: 'Underline', style: 'UNDERLINE'}
  ],
  BLOCK_TYPE_DROPDOWN: [
    {label: 'Normal', style: 'unstyled'},
    {label: 'Heading Large', style: 'header-one'},
    {label: 'Heading Medium', style: 'header-two'},
    {label: 'Heading Small', style: 'header-three'}
  ],
  BLOCK_TYPE_BUTTONS: [
    {label: 'UL', style: 'unordered-list-item'},
    {label: 'OL', style: 'ordered-list-item'}
  ]
};

const StyledViewAllLink = styled(Typography)`
  cursor: pointer;
  font-size: 12px;
  color: #35abdb;
  letter-spacing: 0.15px;
  font-family: "Lato","Helvetica Neue",Arial,Helvetica,sans-serif;
`;

const getTargetUserId = () => { 
  const searchURLParams = new URLSearchParams(window.location.search);
  const id = searchURLParams.get('id') ?? '';
  const userId = id?.length ? id : window.uData?.user_id ?? '';
  return userId;
}

const ViewAllLink = ({ okrPage }) => {
  const { t } = useTranslation();
  const redirectToUrl = () => {
    const searchURLParams = new URLSearchParams(window.location.search);
    const id = searchURLParams.get('id') ?? '';
    let url = `/wb/feedback${id ? '/user/' + id : ''}`;
    if (okrPage) {
      const userId = id?.length ? id : window.uData.user_id;
      url = `/wb/okrs/details/person/${userId}?status=1`;
    }
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <StyledViewAllLink component="span" onClick={redirectToUrl}>
      {t('View all')}
    </StyledViewAllLink>
  )
}

const GiveFeedbackLink = ({ data }) => {
  const { t } = useTranslation();
  const {
    people: {
      info: { user_id }
    }
  } = data;
  const openGiveFeedback = () => {
    window.sendBadgeFun(
      undefined,
      user_id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      'PROFILE_PAGE'
    );
  }

  return (
    <StyledViewAllLink
      component="span"
      onClick={openGiveFeedback}
      sx={{ paddingRight: '10px'}}
    >
      { t('Give feedback') }
    </StyledViewAllLink>
  )
};

/**
 * @class MemberProfile Name that can be plugged anywhere across the app
 * @public
 * @returns {HTMLElement}
 */
class MemberProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Initial state
      data: {people: null},
      loading: true,
      value: RichTextEditor.createValueFromString('', 'html'),
      editable: true,
      imgSrc: '',
      isOwner: false,
      reload: false,
      showMoreAvailable: false,
      defaultBadgeVal: 6,
      selectedBadgeImg: 'holeinone.png',
      badgeView: false,
      modalIsOpen: false,
      croppedImageUrl: null,
      firstUpload: true,
      firstUrl: "",
      crop: {
        aspect: 16/4.5,
        width: 50,
        x: 0,
        y: 0,
        minWidth: 40
      },
      isCropped: false,
      showMoreObjectives: false,
      showMoreKeyResults: false
    };
    this.onChange = this.onChange.bind(this);
    this.getManagerInformation = this.getManagerInformation.bind(this);
    this.toggleEditorState = this.toggleEditorState.bind(this);
    this.sendBadge = this.sendBadge.bind(this);
    this.getMoreBadges = this.getMoreBadges.bind(this);
    this.updateBadgeVal = this.updateBadgeVal.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.openModal = this.openModal.bind(this);
    this.afterOpenModal = this.afterOpenModal.bind(this);
    this.onImageLoaded = this.onImageLoaded.bind(this);
    this.onCropComplete = this.onCropComplete.bind(this);
    this.onCropChange = this.onCropChange.bind(this);
    this.getCroppedImg = this.getCroppedImg.bind(this);
    this.saveCoverImage = this.saveCoverImage.bind(this);
    this.cancelEditorState = this.cancelEditorState.bind(this);
    this.getMoreObjectives = this.getMoreObjectives.bind(this);
    this.getMoreKeyResults = this.getMoreKeyResults.bind(this);
  }

  componentDidMount() {
    this.initComp();
    setTimeout(() => {
      $('.goalDetailsPopup').on('dialogclose', (event) => {
        this.initComp();
      });
    }, 2000);
  }

  componentWillUnmount() {
  }

  formatDate(date) {
    if (!date) {
      return '';
    }
    const options = { month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  initComp(){
    const urlParams = new URLSearchParams(window.location.search);
    const peopleId = urlParams.get('id');
    const refSrc = urlParams.get('refSrc');
    const appendPeopleId = peopleId ? `id=${peopleId}` : '';
    const isEnabledNewFeedback = window?.wbConfig?.isEnabledNewFeedback ?? false;
    const isFeedbackDisabledCompletely = window?.wbConfig?.isFeedbackDisabledCompletely ?? false;
    const peopleUrl = `${wbConfig.baseUrl}/people?${appendPeopleId}&refSrc=${refSrc}&showMoreBadges=true&goalLimit=15&showMoreGoals=true&krOffset=0&krLimit=10&showMoreKR=true&includeCoOwned=1`;

    const promiseAPIs = [axios.get(peopleUrl)];
    if (isEnabledNewFeedback) {
      const userId = peopleId ?? window.uData.user_id;
      if (userId) {
        promiseAPIs.push(axios.get(`${wbConfig.baseUrl}/vnext/hro-api/feedback-received/received/user/${userId}?page=0&pageSize=4`));
      }
    }
    Promise.all(promiseAPIs).then((results) => {
      const data = results[0].data;
      const { info } = data.people;
      let aboutMe = info?.about_me?.about_me || null;
      const feedbacks = results?.[1]?.data || [];
      if (feedbacks?.length) {
        console.log('feedbacks', feedbacks);
        const feedbacksData = feedbacks.map((feedback) => {
          return {
            rank: feedback?.badge?.id ?? '',
            name: feedback?.badge?.name ?? '',
            img_uri: feedback?.badge?.img ?? '',
            from: {
              first_name: feedback.giverUser.firstName,
              last_name: feedback.giverUser.lastName,
            },
            comment: {
              text: DOMPurify.sanitize(feedback?.message ?? ''),
            },
            date: {
              display_time: this.formatDate(feedback.date),
            }
          }
        });
        data.people.momentum.badges = feedbacksData;
      }
      this.setState({
        data,
        profileUserId: info.user_id,
        isOwner: info.is_owner,
        current_user: info.current_user,
        value: aboutMe ? RichTextEditor.createValueFromString(aboutMe, 'html') : RichTextEditor.createEmptyValue(),
        loading: false,
        originalAboutMe: aboutMe ? RichTextEditor.createValueFromString(aboutMe, 'html') : RichTextEditor.createEmptyValue(),
        isFUSEnabled: info.isFUSEnabled,
        appinID: info.appinID,
        accessToken: info.accessToken,
        showMoreAvailable: data.people?.momentum?.showMoreBadges ?? false,
        showMoreObjectives: data.people?.showMoreObjectives ?? false,
        showMoreKeyResults: data.people?.showMoreKeyResults ?? false,
      }, () => {
        this.loadNewBubbleChart();
      });
    });
  }

  onChange(value) {
    this.setState({value});
  };

  /**
   * @function: Loads the team engagement bubbles using D3.
   * Use more white space available on the card when fewer bubbles are displayed
   */
  loadNewBubbleChart() {
    let data = this.state.data.people.engagement.team;
    if(data.length == 0) return;
    data = data.filter(val => {
      return +val.scale > 0;
    });
    let diameter = {width: 350, height: 275}, //max size of the bubbles
      color = d3.scale.ordinal()
        .domain(data.map((d) => d.team_name))
        .range(["#eb2e95", "#6554c0", "#2e5670", "#1990ff", "#ff9395", "#84e183", "#c9afd7", "#aab9c4",
          "#bd10e0", "#bbbe00", "#9b65c0", "#6c8699", "#0078b7", "#52bad5"]); //color category

    let bubble = d3.layout.pack()
      .sort(null)
      .size([diameter.width, diameter.height])
      .padding(2.5);

    let svg = d3.select(".bubbleChart")
      .append("svg:svg")
      .attr("width", diameter.width)
      .attr("height", diameter.height)
      .attr("class", "bubble");

    //Adding the value property required for bubbles
    data = data.map((d) => {
      d.value = parseFloat(d.scale);
      return d;
    });

    //bubbles needs very specific format, convert data to this.
    let nodes = bubble.nodes({children: data}).filter(function (d) {
      return !d.children;
    });

    //setup the chart
    let bubbles = svg.selectAll("circle")
      .data(nodes)
      .enter();
    var tooltip = d3.select(".bubbleChart")
      .append("div")
      .attr('class', 'tooltip-legend')
      .style("position", "absolute")
      .style("z-index", "10")
      .style("visibility", "hidden")
      .style("background", "#000")
      .style('padding', '10px')
      .style('color', '#FFF')
      .text("a simple tooltip");

    //create the bubbles
    bubbles.append('g').attr('class', 'g-bubble').append("svg:circle")
      .attr("r", function (d) {
        return d.r;
      })
      .attr("cx", function (d) {
        return d.x;
      })
      .attr("cy", function (d) {
        return d.y;
      })
      .style("fill", function (d) {
        return color(d.value);
      })
      .on("mouseover", function(d){tooltip.style('bottom', d.y); tooltip.text(d['team_name']); return tooltip.style("visibility", "visible");})
      .on("mousemove", function(d){return tooltip.style("top", (d.y-50)+"px").style("left",(d.x-75)+"px");})
      .on("mouseout", function(){return tooltip.style("visibility", "hidden");})
    //format the text for each bubble
    bubbles.append("text")
      .attr("x", function (d) {
        return d.x;
      })
      .attr("y", function (d) {
        return d.y;
      })
      .attr("text-anchor", "middle")
      .text(function (d) {
        return d["team_name"];
      })
      .style({
        "fill": "white",
        "font-family": "Lato, Helvetica Neue, Helvetica, Arial, san-serif",
        "font-size": "12px"
      }).each(wrap);
    function wrap(d){
      var self = d3.select(this),
            width = d.r * 2,
            padding = 5,
            textLength = self.node().getComputedTextLength(),
            text = self.text();
        while (textLength > (width - 2 * padding) && text.length > 0) {
            text = text.slice(0, -1);
            self.text(text + '...');
            textLength = self.node().getComputedTextLength();
        }
    }
  }

  /**
   * @function: Gets the information of the users manager.
   * @param: people object containing the current member profile information
   */
  getManagerInformation({people}) {
    // #Todo: Handle the people object better. If empty or required info not available, falsify gracefully.
    let reportArr = people.info.manager;
    if (reportArr) {
      // If manager is available, then we populate the dotted and direct report
      return reportArr.map((val, idx) => {
        if (val.role == "1") {
          return (
            <div key={idx} className={"direct-report"}>
              <p>
                <Trans 
                  i18nKey="member.reports.directly"
                  components={{
                    ManagerLink: (
                      <a
                        target="_blank"
                        href={`/wb/memberProfile/index?id=${val.user_id}`}
                      />
                    )
                  }}
                  values={{
                    firstName: val.first_name,
                    lastName: val.last_name
                  }}
                />
              </p>
            </div>
          )
        } else {
          return (
            <div key={idx} className={"direct-report"}>
              <p>
                <Trans 
                  i18nKey="member.reports.dotted"
                  components={{
                    ManagerLink: (
                      <a
                        target="_blank"
                        href={`/wb/memberProfile/index?id=${val.user_id}`}
                      />
                    )
                  }}
                  values={{
                    firstName: val.first_name,
                    lastName: val.last_name
                  }}
                />
              </p>
            </div>
          )
        }
      });
    }
  }

  /**
   * @function: Gets the list of badges when show more is clicked
   * @default: Returns all badges offset by 3. (since by default first 3 are shown on page load)
   */
  getMoreBadges(){
    const urlParams = new URLSearchParams(window.location.search);
    const peopleId = urlParams.get('id');
    const appendPeopleId = peopleId ? `id=${peopleId}` : '';
    const peopleUrl = `${wbConfig.baseUrl}/people?${appendPeopleId}&resource=momentum&badgeLimit=100&badgeOffset=3&notIncludeWs=true`;
    let _this = this;
    $.post(peopleUrl, function(data){
      let newBadgeArr = {..._this.state.data}
      newBadgeArr.people.momentum.badges = newBadgeArr.people.momentum.badges.concat(data.people.momentum.badges);
      _this.setState({newBadgeArr, showMoreAvailable: false});
    })
  }

  sendBadge(){
    const urlParams = new URLSearchParams(window.location.search);
    const peopleId = urlParams.get('id');
    let comment = this.refs.badgeTextarea.value || "";
    if(comment == "") return;
    let rank = this.state.defaultBadgeVal;
    let data = {
      "member_id": peopleId,
      "user_id": this.state.current_user,
      "team_id": null,
      "goalId": null,
      "act_id": null,
      "rank": rank,
      "comment": comment,
      "analytic_name": 'PROFILE_PAGE'
    };

    let _this = this;
    $.post(`/wb/team/saveTeamFeedback`, data, function(resp) {
      if(resp.success == "ok"){
        toastr['success']('Badge sent!');
        const urlParams = new URLSearchParams(window.location.search);
        const peopleId = urlParams.get('id');
        let peopleUrl = peopleId ? `${wbConfig.baseUrl}/people?id=${peopleId}&resource=momentum&badgeLimit=1&badgeOffset=0`: `${wbConfig.baseUrl}/people?resource=momentum&badgeLimit=1&badgeOffset=0`;
        $.post(peopleUrl, function(data){
          let newBadgeArr = {..._this.state.data}
          newBadgeArr.people.momentum.badges = data.people.momentum.badges.concat(newBadgeArr.people.momentum.badges);
          _this.setState({newBadgeArr});
          _this.refs.badgeTextarea.value = "";
        })
      }
    })
  }

  updateBadgeVal(defaultBadgeVal, selectedBadgeImg){
    this.setState({
      defaultBadgeVal: defaultBadgeVal,
      selectedBadgeImg: selectedBadgeImg,
      badgeView: false
    });
  }

  closePopup(){
    this.setState({
      badgeView: false
    });
  }

  handleChangePhoto = () => {
    let file = this.refs.changeFile.files[0];
    let reader = new FileReader();
    let url = reader.readAsDataURL(file);
    if(!validateImageFileExtension(file.name))  return false;
    reader.onloadend = () => {
      this.setState({
        imgSrc: reader.result.toString(),
        file: file,
        firstUpload: false
      });
      this.refs.changeFile.value = "";
      return false;
    };
  };

  openCoverImageDialog = (imgSrc) => {
    this.setState({
      modalIsOpen: true,
      imgSrc: imgSrc,
      firstUrl: imgSrc,
      cropType: 'cover',
      crop: {
        aspect: 16/4.5,
        width: 50,
        x: 0,
        y: 0,
        minWidth: 50
      }
    })
  }

  updateCoverImage = () => {
    let file = this.refs.file.files[0];
    console.log(file);
    let reader = new FileReader();
    let url = reader.readAsDataURL(file);

    reader.onloadend = () => {
      this.refs.file.value = '';
      this.setState({
          imgSrc: reader.result.toString(),
          modalIsOpen: true,
          cropType: 'cover',
          crop: {
            aspect: 16/4.5,
            width: 50,
            x: 0,
            y: 0,
            minWidth: 50
          }
      });
      return false;
    };
  };

  openProfileDialog = (imgSrc) => {
    this.setState({
      modalIsOpen: true,
      imgSrc: imgSrc,
      cropType: 'profile',
      firstUrl: imgSrc,
      crop: {
        aspect: 1,
        width: 50,
        x: 0,
        y: 0,
        minWidth: 40,
        minHeight: 40
      }
    })
  };

  updateProfileImage = () => {
    let file = this.refs.profileFile.files[0];
    let reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = (e) => {
      this.refs.profileFile.value = '';
      this.setState({
          imgSrc: reader.result.toString(),
          modalIsOpen: true,
          cropType: 'profile'
      });
      return false;
    };
  }

  saveCoverImage() {
    let {file, profileUserId, cropType, isFUSEnabled, appinID, accessToken} = this.state;
    let saveUrl;
    let profileReload = 0;
    let coverReload = 0;
    if(cropType === "profile"){
      saveUrl = `${wbConfig.baseUrl}/user/profile/updateProfilePic`
      profileReload = 1;
    } else {
      saveUrl = `${wbConfig.baseUrl}/people/setCoverImage`;
      coverReload = 1;
    }
    let _this = this;
    if (isFUSEnabled) {
      let request_id = uuidv4();
      let orgId = uData.org_id;
      updateMemberProfileAndCover(file, saveUrl, {appinID, request_id, accessToken, profileUserId, orgId}, _this.state.isCropped, { profileReload, coverReload });
    } else {
        $.ajax({
          url: wbConfig.baseUrl + '/workboard/GetS3Data',
          type: 'POST',
          dataType: 'json',
          data: {authVal: 'public-read'}
        }).done(function(resp){
          let key = replaceS3Key(resp.key, file.name);
          let asc = resp.asc;
          if(resp.encrypt_type !== 'azure:sse'){
            asc = 's3';
            key = resp;
          }
          uploadFileToAzureStorage(asc, key, file, null, 'anonymous').then((result) =>{
            let profileParams = {cover_image:result.filePath, user_id: profileUserId};
            if(cropType === "profile"){
              profileParams = {picture:result.filePath};
            }
            $.ajax({
              url: saveUrl,
              type: "POST",
              data: profileParams,
              dataType: "json",
              async: true,
              success(res) {
                toastr.success("Profile picture updated");
                // Setting a timeout to handle the window location reload to refresh images across app
                setTimeout(() => {
                  window.location.reload();
                }, 200);
              }
            });
          }).catch(e => {
            toastr['success'](`${_this.state.cropType} image updated`);
            _this.setState({modalIsOpen: false}, () => {
              window.location.reload();
            });
          });
        })
      }
  }

  toggleEditorState(){
    if(!this.state.editable){
      let postData = {
        "user_id": this.state.profileUserId,
        "about_me": this.state.value.toString('html')
      };
      $.post('/wb/people/setAboutMe', postData, function(resp){
        console.log(resp);
      })
    }
    this.setState(prevState => ({
      editable: !prevState.editable,
      originalAboutMe: this.state.value.toString('html')
    }))
  }

  cancelEditorState(){
    this.setState(prevState => ({
      editable: !prevState.editable,
      value: this.state.originalAboutMe ? RichTextEditor.createValueFromString(this.state.originalAboutMe, 'html') : RichTextEditor.createEmptyValue()
    }))
  }

  openModal() {
    this.setState({modalIsOpen: true});
  }

  afterOpenModal() {
    // references are now sync'd and can be accessed.
    // this.subtitle.style.color = '#f00';
  }

  closeModal = () => {
    this.setState({modalIsOpen: false});
  }

  onImageLoaded(image, pixelCrop){
    this.imageRef = image;

    // Make the library regenerate aspect crops if loading new images.
    const { crop } = this.state;

    if (crop.aspect && crop.height && crop.width) {
      this.setState({
        crop: { ...crop, height: null },
      });
    } else {
      this.makeClientCrop(crop, pixelCrop);
    }
  };

  onCropComplete(crop, pixelCrop){
    this.makeClientCrop(crop, pixelCrop);
  };

  onCropChange(crop){
    this.setState({ crop });
  };

  async makeClientCrop(crop, pixelCrop) {
    if (this.imageRef && crop.width && crop.height) {
      const [croppedImageUrl, file] = await this.getCroppedImg(
        this.imageRef,
        pixelCrop,
        'newFile.jpeg',
      );
      this.setState({ croppedImageUrl, file });
      this.setState({ isCropped: true })
    }
  }

  getCroppedImg(image, pixelCrop, fileName) {
    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    if(this.state.firstUpload) {
      let img = new Image;
      return new Promise((resolve, reject) => {
        img.onload = function() {     // draw in image
          ctx.drawImage(
            img,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height,
          );
          canvas.toBlob(blob => {
            blob.name = fileName;
            window.URL.revokeObjectURL(this.fileUrl);
            this.fileUrl = window.URL.createObjectURL(blob);
            let file;
            if(window.navigator.userAgent.indexOf('Edge') > -1 || browserDetection() === "ie"){
              file = new Blob([blob], {type: 'image/jpeg'});
              file.name = fileName;
            } else {
              file = new File([blob], "testing");
            }
            resolve([this.fileUrl, file]);
          }, 'image/jpeg');
        };
        img.crossOrigin = "anonymous";              // if from different origin
        img.src = this.state.firstUrl;
      });
    } else {
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          blob.name = fileName;
          window.URL.revokeObjectURL(this.fileUrl);
          this.fileUrl = window.URL.createObjectURL(blob);
          let file;
          if(window.navigator.userAgent.indexOf('Edge') > -1 || browserDetection() === "ie"){
            file = new Blob([blob], {type: 'image/jpeg'});
            file.name = fileName;
          } else {
            file = new File([blob], "testing");
          }
          resolve([this.fileUrl, file]);
        }, 'image/jpeg');
      });
    }
  }


  deletePhoto = () => {
    let _this = this;
    $.post(`${wbConfig.baseUrl}/user/profile/deleteUserProfilePicture`, function(response){
      let resp = JSON.parse(response);
      if(resp.success){
        toastr['success']('Profile picture deleted!');
        _this.closeModal();
        _this.initComp();
        window.location.reload();
      }
    })
  };

  deleteCoverPhoto = () => {
    let _this = this;
    $.post(`${wbConfig.baseUrl}/people/deleteProfileCoverImage`, function(response){
      let resp = JSON.parse(response);
      if(resp.success){
        toastr['success']('Cover image deleted!');
        _this.closeModal();
        _this.initComp();
      } else {
        toastr['error']('Cover image could not be deleted. Please refresh and try again.');
      }
    })
  }

  getMoreObjectives(){
    const urlParams = new URLSearchParams(window.location.search);
    const peopleId = urlParams.get('id');
    const appendPeopleId = peopleId ? `id=${peopleId}` : '';
    const offset = this.state.data.people.objectives.length;
    const peopleUrl = `${wbConfig.baseUrl}/people?${appendPeopleId}&resource=objectives&goalOffset=${offset}&goalLimit=15&showMoreGoals=true`;
    let _this = this;
    $.post(peopleUrl, function(data){
      let newBadgeArr = {..._this.state.data}
      newBadgeArr.people.objectives = newBadgeArr.people.objectives.concat(data.people.objectives);
      _this.setState({newBadgeArr, showMoreObjectives: data.people?.showMoreObjectives ?? false});
    })
  }

  getMoreKeyResults(){
    const urlParams = new URLSearchParams(window.location.search);
    const peopleId = urlParams.get('id');
    const appendPeopleId = peopleId ? `id=${peopleId}` : '';
    const offset = this.state.data.people.key_results.length;
    const peopleUrl = `${wbConfig.baseUrl}/people?${appendPeopleId}&resource=keyresults&krOffset=${offset}&krLimit=10&showMoreKR=true`;
    let _this = this;
    $.post(peopleUrl, function(data){
      let newBadgeArr = {..._this.state.data}
      newBadgeArr.people.key_results = newBadgeArr.people.key_results.concat(data.people.key_results);
      _this.setState({newBadgeArr, showMoreKeyResults: data.people?.showMoreKeyResults ?? false});
    })
  }

  render() {
    const { t } =this.props;
    const {loading} = this.state;
    const isEnabledNewFeedback = window?.wbConfig?.isEnabledNewFeedback ?? false;
    const isFeedbackDisabledCompletely = window?.wbConfig?.isFeedbackDisabledCompletely ?? false;
    if (loading) {
      return (
        <div className="member-profile">
          <MyLoader/>
        </div>
      )
    } else {
      const {data: {people}, showMoreKeyResults } = this.state;
      customStyles['maxHeight'] = window.outerHeight * 0.65;
      const executeModule = this.props.executeModule || false;
      const goalModule = this.props.goalModule || false;
      // A digital agent's profile shows only its action items and feedback.
      const isAgent = this.state.data.people.info.agent_type > 0;
      // Agents keep two cards, so they share the page width 70/30 instead of the fixed 790px row.
      const agentRow = { width: '100%', display: 'flex', gap: '12px' };
      const agentCard = (grow) => ({ flex: `${grow} 1 0`, width: 'auto', height: 'auto', minHeight: '345px', margin: 0 });
      const keyResultsLength = people?.key_results?.length ?? 0;
      const directManager = people?.info?.manager?.length && people?.info.manager.find((user) =>
          +user.user_id === +people?.info.current_user && +user.role === 1);
      const isDirectManager = directManager?.user_id ?? false;
      const managerUser = isDirectManager ? {
        id: directManager.user_id,
        fullName: `${directManager.first_name} ${directManager.last_name}`,
        picture: directManager.picture,
        isReadOnly: false,
      } : {
        id: window.uData.user_id,
        fullName: `${window.uData.first_name} ${window.uData.last_name}`,
        picture: window.uData.picture,
        isReadOnly: false,
      };

      return (
        <div className="member-profile">
          <Modal
            isOpen={this.state.modalIsOpen}
            onAfterOpen={this.afterOpenModal}
            onRequestClose={this.closeModal}
            style={customStyles}
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            contentLabel={t("Crop Image Modal")}>
            <h3 style={{display: "inline-block", width: 'auto',
              textTransform: 'capitalize', color: 'rgba(0, 0, 0, 0.75)', fontWeight: 'bold'}}>
              {t("Edit")} {this.state.cropType} {t("Image")}
            </h3>
            <i className="icon icon-outline-close-24px cursor-pointer"
              style={{marginRight: '5px', float: 'right'}} onClick={this.closeModal} />
            <div className={"wb-row"}>
              <div className={"col-md-12 text-center"}>
                <ReactCrop
                  src={this.state.imgSrc}
                  crop={this.state.crop}
                  onImageLoaded={this.onImageLoaded}
                  onComplete={this.onCropComplete}
                  onChange={this.onCropChange}
                />
              </div>
              <br />
              <div className={"col-md-12 m-t-1"}>
                {this.state.cropType === "profile" && <a onClick={this.deletePhoto}
                                                         className={"left delete-user-photo"}
                                                         href={"#"}>{t("Delete photo")}</a>
                }

                {this.state.cropType === "cover" && <a onClick={this.deleteCoverPhoto}
                                                         className={"left delete-user-photo"}
                                                         href={"#"}>{t("Delete cover image")}</a>
                }

                <button onClick={this.saveCoverImage} type="submit"
                  className={"gbl-btn right"}>{t("Save")}</button>

                <div className={"mp-profile-change-btn-wrapper wb-pointer right"}>
                  <button className={"mp-upload-btn wb-pointer"}>
                    {t("Change photo")}
                  </button>
                  <input id="change-file-upload" type="file" accept="image/*"
                         title={t("Click here to change your image")}
                         onChange={this.handleChangePhoto}
                         ref={"changeFile"} className={"mp-change-file-input"}/>
                </div>
              </div>
            </div>
          </Modal>
          <div className="mp-header cover">
            <div className="mp-header-inner">
              <div className="mp-header-image">
                <div className="mp-header-image-main">
                  <img
                    loading="lazy"
                    fetchPriority="low"
                    src={`${people.info.cover_image.cover_image}`}/>
                </div>
              </div>
              <div className="mp-container mp-profile-details">
                <div className="mp-profile-picture">
                  <div className="mp-profile-avatar fitProfileImage">
                    {this.state.isOwner && <div className={"mp-profile-upload-btn-wrapper wb-pointer"}>
                      <button className={"mp-upload-btn"} onClick={() => this.openProfileDialog(people.info.picture)}>
                        <i className="icon icon-outline-edit-24px"
                           style={{marginRight: '5px'}} />
                      </button>
                    </div>}
                    <img
                      alt={t("Profile Picture")}
                      src={`${people.info.picture}`}
                    />
                  </div>
                  <div className="mp-member-details">
                    <span className="mp-member-name">{`${people.info.first_name} ${people.info.last_name}`}</span>
                    <span className="mp-member-designation">{`${people.info.title}`}</span>
                    <StyledMemberReportWrapper sx={{ marginTop: people?.info?.title?.length ? 2 : 5 }}>
                      <span className={"mp-member-report"}>
                        {this.getManagerInformation({people})}
                      </span>
                      <span className={"mp-manager-lens"} style={{ minWidth: '180px' }}>
                        <ProfileManagerLens
                          userId={+people.info.user_id}
                          manager={managerUser}
                          isDirectManager={isDirectManager}
                        />
                      </span>
                     </StyledMemberReportWrapper>
                  </div>
                </div>
                {this.state.isOwner && this.state.editable && <i onClick={this.toggleEditorState} className="icon icon-outline-edit-24px about-me-edit wb-pointer" style={{marginRight: '5px', float: 'right'}} />}
                <div className={(!this.state.editable ? "mp-editor-editable" : "") + " mp-header-description"}>
                  {/* Make the editor available only if the page is user owned and is editable */}
                  {this.state.isOwner ? <RichTextEditor placeholder={t("Tell us something about yourself and what you do everyday! What are your superpowers?")}
                                   editorClassName="wb-rte-editor" readOnly={this.state.editable} toolbarConfig={toolbarConfig}
                                   value={this.state.value} onChange={this.onChange}/>: <RichTextEditor readOnly={true} value={this.state.value} />}
                </div>
                <div className={"col-md-12 no-padding"}>
                  {!this.state.editable && <button onClick={this.toggleEditorState} className={'gbl-btn right'}>{t('Save')}</button>}
                  {!this.state.editable && <button onClick={this.cancelEditorState} className={'gbl-btn gbl-btn-cancel right m-r-1'}>{t('Cancel')}</button>}
                </div>
              </div>

            </div>
          </div>
          {/* Adding the upload header button */}
          {this.state.isOwner && <div className={"mp-upload-btn-wrapper wb-pointer"}>
            <button className={"mp-upload-btn"}  onClick={() => this.openCoverImageDialog(people.info.cover_image.cover_image)}>
              <i className="icon icon-outline-edit-24px"
                 style={{marginRight: '5px'}} />
            </button>
            {/*<input type="file" title="Click here to change your cover image" ref={"file"} onChange={this.updateCoverImage}*/}
            {/*    className={"mp-upload-input wb-pointer"} name="pic" accept="image/*" />*/}
          </div>}
          <div className={"mp-left-section"} style={isAgent ? { width: '100%' } : undefined}>
            {/* TEAM ENGAGEMENT CARD */}
            <div className="mp-profile-info" style={isAgent ? agentRow : undefined}>
              <div 
                className="mp-card"
                style={{
                  ...(isFeedbackDisabledCompletely === true ? { width: '772px' } : {}),
                  ...(isAgent ? agentCard(isFeedbackDisabledCompletely ? 1 : 7) : {}),
                }}
              >
                {isAgent ? (
                  <AgentActionItems agentId={Number(this.state.data.people.info.user_id)} />
                ) : (
                  <div className="mp-container">
                    <h3 className="mp-card-header">{t("TEAM ENGAGEMENT")}
                      <div className="tooltip"><i className={"mp-help-icon icon icon-outline-help_outline-14px"} />
                        <span className="tooltiptext">{t("Team Engagement is a measure of all activity for a person’s most active teams in the last 3 months.")}
                        {t("The team size distribution takes objective and key result ownership into consideration, as well as all action item activity.")}</span>
                      </div>
                    </h3>
                    <div className="bubbleChart" style={{
                      position: 'relative',
                      overflow: 'hidden',
                      ...(isFeedbackDisabledCompletely === true ? { 'text-align': 'center' } : {}),
                    }} />
                    {this.state.data.people.engagement.team.length == 0 && <div style={{margin: '95px 0 0 0'}}><img className={"no-data-img"} src={"/wb/images/member-profile/engagement_blank.png"} /><p className={"no-data-text"}>{t("No recent engagement.")}</p></div>}
                  </div>
                )}
              </div>

              {/* MOMENTUM CARD */}
              {!isFeedbackDisabledCompletely && (
                <div className="mp-card" style={isAgent ? agentCard(3) : undefined}>
                <div className="mp-container">
                  <div className={"wb-row"}>
                    <div className={"col-md-6"}>
                      <h3 className="mp-card-header">{t("Feedback")}
                        { executeModule && <div className="tooltip"><i className={"mp-help-icon icon icon-outline-help_outline-14px"} />
                          <span className="tooltiptext">{`${t('List of most recent workstreams is a list of workstreams that the user has last visited, or has updated an action item on.')} ${t('You will only be able to see the workstreams for which you have viewing permissions.')}`}</span>
                        </div>
                        }
                      </h3>
                    </div>
                    <div className={"col-md-6 text-right mp-see-more"}>
                      {
                        isEnabledNewFeedback && (
                          <>
                          <GiveFeedbackLink data={this.state.data} />
                          <ViewAllLink />
                          </>
                        )
                      }
                      {this.state.showMoreAvailable && !isEnabledNewFeedback && <a className={"wb-pointer"} onClick={this.getMoreBadges}>{t("See more")}</a>}
                    </div>
                  </div>
                  {!this.state.isOwner && !isEnabledNewFeedback && <div className={"wb-row"}>
                    <div className="col-md-12">
                      <input value={this.state.defaultBadgeVal} onClick={() => this.setState(prevState => ({badgeView: !prevState.badgeView}))}
                             className={"hidden-textbox"} type={"checkbox"} checked={this.state.badgeView} />
                      <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/${this.state.selectedBadgeImg}`} className="selected-badge-img" />
                      <div className="tooltip show-selection-badge">
                        <EnhancedComponent closePopup={this.closePopup} updateBadgeVal={this.updateBadgeVal} />
                      </div>
                      <div className={"mp-textarea-case"}>
                        <textarea placeholder={t("Give a shoutout")}
                          ref={"badgeTextarea"}
                          className={"mp-badge-textarea"} />
                          <img alt={t("Send")} onClick={this.sendBadge} src="/wb/images/member-profile/send.png"
                            className={"send-badge-img wb-pointer"} />
                      </div>
                    </div>
                  </div>}
                  <div className={(this.state.isOwner ? "momentum-height-lg": "momentum-height-sm") + " mp-ai-row-scroll"}>
                    {people.momentum && people.momentum.badges && people.momentum.badges.map((item, idx) => {
                      if(item.rank != "32"){
                        return (
                          <div key={idx} className={"mp-ai-row wb-row"}>
                            <div className={"col-sm-2"}>
                              <img alt={t("feedback")} src={`/wb/${item.img_uri}`}/>
                            </div>
                            <div className={"col-sm-10"} style={{paddingLeft: 0}}>
                              <p className={"mp-sub-desc"}>
                                {`${item.date.display_time} from ${item.from.first_name} ${item.from.last_name}`}</p>
                              <b className={"truncateText"}>{`${item.name}`}</b>
                              <p className={"truncate3"} dangerouslySetInnerHTML={{__html: item.comment.text}} />
                            </div>
                          </div>
                        )
                      }
                    })}
                    {people.momentum && people.momentum.badges && people.momentum.badges.length == 0 && <div><img className={"no-data-img"} src={"/wb/images/member-profile/badges_blank.png"} /><p className={"no-data-text"}>{t("No feedback received yet")}</p></div>}

                    {/* SHOW WORKSTREAM ONLY IF THERE ARE ITEMS TO SHOW */}
                    {executeModule && people.momentum && people.momentum.recent_ws && people.momentum.recent_ws.length > 0 && <div className={"mp-ai-row wb-row"}>
                      <div className={"col-sm-2"}>
                        <img alt={t("user-image")} className="fitProfileImage" src={`${people.info.picture}`}/>
                      </div>
                      <div className={"col-sm-10 no-padding"}>
                        {people.info.first_name} {`${t('was most recently working on')}:`}
                        <ul className={"col-sm-10 ws_ul"}>
                          {people.momentum && people.momentum.recent_ws && people.momentum.recent_ws.map((item, idx) => {
                            if(idx > 4) return;
                            return (
                              <li key={idx}><a target="_blank"
                                href={`${wbConfig.baseUrl}/activity/workstreams?id=${item.ws_id}`}> {item.ws_name}</a></li>)}
                            )}
                        </ul>
                      </div>
                    </div>}
                  </div>
                </div>
                </div>
              )}

              {/* OBJECTIVES CARD */}
              { goalModule && !isAgent && <div className="mp-card-small mp-table-css">
                <div className="mp-container mp-card-objectives">
                  <div className="mp-container">
                    <div className={"wb-row"}>
                      <div className={"col-md-6"}>
                      <h3 className="mp-card-header" style={{marginBottom: '10px',marginTop:"-5px"}}>{t('OBJECTIVES')}</h3>
                      </div>
                      <div className={"col-md-6 text-right mp-see-more"} style={{ marginTop: '8px'}}>
                        { !!people.objectives?.length && <ViewAllLink okrPage />}
                      </div>
                    </div>
                  </div>
                  {people.objectives && people.objectives.map((item, idx) => {
                    if(idx != 0 && (idx + 1) % 3 == 0){
                      return [
                        <div className={"obj-card wb-pointer"} key={idx} onClick={() => WbEventHandlerMixins.goalPage(item)}>
                          <p className={"obj-progress"} style={{color: item.color}}>{parseInt(item.progress) < 0 ? 0 : item.progress}%</p>
                          <p className={'obj-description'}>
                            {item.goal}
                          </p>
                          <p className={'obj-days-left'}>{parseInt(item.days_left) < 0 ? `0 ${t('days left')}`: `${item.days_left} ${t('days left')}`}</p>
                        </div>,
                        <br />
                      ]
                    } else {
                      return (
                        <div className={"obj-card wb-pointer"} key={idx} onClick={() => WbEventHandlerMixins.goalPage(item)}>
                          <p className={"obj-progress"} style={{color: item.color}}>{parseInt(item.progress) < 0 ? 0 : item.progress}%</p>
                          <p className={'obj-description'}>
                            {item.goal}
                          </p>
                          <p className={'obj-days-left'}>{parseInt(item.days_left) < 0 ? `0 ${t('days left')}`: `${item.days_left} ${t('days left')}`}</p>
                        </div>
                      )
                    }
                  })}
                  {people.objectives && people.objectives.length == 0 && <div style={{margin: '35px 0 0 0'}}><img className={"no-data-img"} src={`${wbConfig.imageCdnUrl}/images/member-profile/objectives_blank.png`} /><p className={"no-data-text"}>{t("No objectives yet")}</p></div>}
                  {
                   this.state.showMoreObjectives && (
                    <div className={"col-md-12 text-right mp-see-more"} style={{ marginBottom: '15px'}}>
                      <a className={"wb-pointer"} onClick={this.getMoreObjectives}>{t("See more")}</a>
                    </div>
                    )
                  }

                </div>
              </div>
              }
              {/* TOTAL IMPACT CARD */}
              {!isAgent && (
              <div className="mp-card-small">
                <div className="mp-container">
                  <h3 className="mp-card-header">{t("TOTAL IMPACT")}</h3>
                  { goalModule && <div className={"wb-row impact-row"}>
                    <span className={"col-md-2 impact-value"}>
                      {people['impact']['key_results']}
                    </span>
                    <span className={"col-md-10 impact-desc"}>
                  {t('Key results')}
                </span>
            </div> }
                  { goalModule && <div className={"wb-row impact-row"}>
                <span className={"col-md-2 impact-value"} style={{color: people['impact']['results_achieved']['color']}}>
                  {people['impact']['results_achieved']['progress']}%
                </span>
                    <span className={"col-md-10 impact-desc"}>
                  {t('Results achieved')}
                </span>
            </div> }
                 { executeModule && <div className={"wb-row impact-row"}>
                <span className={"col-md-2 impact-value"} style={{color: "#27b87b"}}>
                  {people['impact']['ai']}
                </span>
                    <span className={"col-md-10 impact-desc"}>
                  {t("Actions complete")}
                </span>
                  </div>
                }
                  <div className={"wb-row impact-row"}>
                <span className={"col-md-2 impact-value"} style={{color: "#52bad5"}}>
                  {people['impact']['badges_received']}
                </span>
                    <span className={"col-md-10 impact-desc"}>
                  {t("Badges received")}
                </span>
                  </div>
                  <div className={"wb-row impact-row"}>
                <span className={"col-md-2 impact-value"} style={{color: "#52bad5"}}>
                  {people['impact']['comment_and_badges_given']}
                </span>
                <span className={"col-md-10 impact-desc"}>
                  {t("Comments and badges given")}
                </span>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          {!isAgent && (
          <div className={"mp-right-section"}>
            { goalModule && <div className="mp-profile-kr-block">
              <div className="mp-container">
                <div className={"wb-row"}>
                  <div className={"col-sm-12"}>
                    <div className={"col-sm-8 no-padding"}>
                      <h3 className="mp-card-header" style={{ fontSize: "12px"}}>
                        {t('RESULTS')} {t("THIS PERIOD")}
                      </h3>
                    </div>
                    <div className={"col-sm-4 text-right"} style={{ padding: "12px 0px 0px 0px"}}>
                    { !!people.key_results?.length && <ViewAllLink okrPage />}
                    </div>
                  </div>
                </div>
                {people.key_results && people.key_results.map((item, idx) => {
                  return (
                    <div className={"wb-row wb-pointer"} key={idx} onClick={() => WbEventHandlerMixins.goalMetricPage(item, '', 'metric')}>
                      <div className={"col-sm-12 no-padding key_result_row"}>
                        <div className={"col-sm-8 wb-word-break"}>
                          {item.name}
                        </div>
                        <div className={"col-sm-4 no-padding text-right kr_padding"}>
                          <span className={"mp-kr-target-achieved"} style={{color: item.color}}>
                            {item.achieved_target}
                          </span>
                          <br/>
                          <span className={"mp-kr-total-target"}>
                          {t("of")} {item.target}
                          </span>
                        </div>
                      </div>
                      {/* #TODO: Move the length lookup elsewhere to save time in doing this in every loop */}
                      {(keyResultsLength != idx+1 || (showMoreKeyResults && keyResultsLength == idx + 1)) && <div className={"col-xs-12"}>
                        <hr/>
                      </div>}
                    </div>
                  )
                })}
                {!people.key_results && <div><img className={"no-data-img"} src={`${wbConfig.imageCdnUrl}/images/member-profile/results_blank.png`} /><p className={"no-data-text"}>{t("No key results yet")}</p></div>}
                {
                   showMoreKeyResults && (
                    <div className="wb-row wb-pointer">
                      <div className={"col-md-12 text-right mp-see-more"} style={{ marginBottom: '15px'}}>
                        <a className={"wb-pointer"} onClick={this.getMoreKeyResults}>{t("See more")}</a>
                      </div>
                    </div>
                    )
                  }
              </div>
            </div>
            }
            { executeModule && <div className={"mp-execution-velocity"}>
              <div className="mp-container">
                <h3 className="mp-card-header">{t("EXECUTION VELOCITY")}</h3>
                <br/>
                <div className={"wb-row"}>
                  <div className={"col-sm-6"}>
                    <h1 className={"velocity-in-progress"}>{people['velocity']['execution']['count']['1'] || 0} <img
                      src={"/wb/images/icons/doing@3x.png"}/></h1>
                    <p>{t("action items in flight")}</p>
                  </div>
                  <div className={"col-sm-6"}>
                    <h1 className={"velocity-done"}>{people['velocity']['execution']['count']['0'] || 0} <img
                      src={"/wb/images/subscription/green_checkmark.png"}/></h1>
                    <p>{t("actions complete")}</p>
                  </div>
                </div>
              </div>
              </div> }
             <Suspense fallback={null}>
               <ErrorBoundary fallbackRender={({ error }) => { console.error(error); return null; }}>
                 <CheckInsCard targetUserId={getTargetUserId()} />
               </ErrorBoundary>
             </Suspense>
          </div>
          )}
        </div>
      );
    }
  }
}

class BadgeComponent extends React.Component{
  constructor(props){
    super(props);
  }
  myClickOutsideHandler(evt) {
    // ...handling code goes here...
    this.props.closePopup()
  }
  render(){
    return(
      <ul className={"tooltiptext"}>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(6, 'holeinone.png')}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/holeinone.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Hole in one")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(7, 'magician.png')}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/magician.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Magician")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(9, 'goodjob.png')}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/goodjob.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Good Job")}!</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(22, "awesome.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/awesome.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Awesome")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(23, "gifted.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/gifted.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Gifted")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(25, "sweet.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/sweet.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Sweet")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(28, "thankyou.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/thankyou.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Thank You")}!</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(39, "rockstar.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/rockstar.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Rockstar")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(35, "highfive.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/highfive.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("High five")}</span>
        </li>
        <li className={"badge-list"} onClick={() => this.props.updateBadgeVal(40, "lifesaver.png")}>
          <img src={`${wbConfig.imageCdnUrl}/images/icons/badges/lifesaver.png`} className="mp-img-badge" />
          <span className={"badge-name"}>{i18n.t("Life saver")}</span>
        </li>
      </ul>
    )
  }
}

const clickOutsideConfig = {
  handleClickOutside: function(instance) {
    return instance.myClickOutsideHandler;
  }
};

let EnhancedComponent = onClickOutside(BadgeComponent, clickOutsideConfig);

const MemberProfileHOC = withTranslation()(MemberProfile);

let initMemberProfile = function(data = {}){
    let mountId = document.getElementById('member-profile');
    if(mountId){
      const root = createRoot(mountId);
        root.render(
            <Router>
              <Suspense fallback="loading...">
                  <I18nextProvider i18n={i18n}>
                      <MemberProfileHOC {...data} />
                  </I18nextProvider>
              </Suspense>
            </Router>,
            document.getElementById('member-profile')
        );
    }
};

window.initMemberProfile = initMemberProfile;

// ReactDOM.render(<MemberProfile/>, document.getElementById('member-profile'));